import './searchResultDetails.scss';
import './details/details.scss';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useSelectedDocContext, useStateDocDetailsExpanded, useStateResultDetailsTab} from '../../search-hooks';
import {changeUrlAction, expandDocumentDetailsAction, switchResultDetailsTabAction} from '../../duck/search-actions';
import {BaseDataRow} from './details/base-data-section/baseDataRow';
import {TagsRow} from './details/tagsRow';
import {ProductDocument, TabType} from '../../search-types';
import classNames from 'clsx';
import {HibuButton} from '../../../common/hibuButton/hibuButton';
import {Icons} from '../../../icons/icons';
import {FileTypeColumnControl} from '../search-result-list/search-result-item/column-controls/fileType-column-control';
import {DeepReadonly} from 'utility-types';
import {Button} from 'react-bootstrap';
import React from 'react';
import {ContentSection} from './content/contentSection';
import {SpotlightSection} from './spotlight/spotlightSection';
import {SvgIcon} from '../../../icons/svgIcon';
import {DuplicatesList} from './details/duplicatesList/duplicatesList';
import {SearchPageType} from '../../search-config';
import {useHistory} from 'react-router-dom';

export const SearchResultDetails = React.memo(() => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const selectedDoc = useSelectedDocContext();
    const searchConfig = useSelector((state) => state.config.search);
    const currentResultTab = useStateResultDetailsTab();
    const showTechnicalDetails = useSelector((state) => state.settings.technicalDetails);
    const tabToRender: TabType = searchConfig.enabledDetailsTabs.includes(currentResultTab)
        ? currentResultTab
        : searchConfig.enabledDetailsTabs[0];
    const showDetails = useStateDocDetailsExpanded();
    const history = useHistory();

    const onExpand = () => {
        const newSate = !showDetails;
        dispatch(expandDocumentDetailsAction(newSate));
    };

    if (!selectedDoc) {
        return null;
    }

    const onClick = (e) => {
        dispatch(switchResultDetailsTabAction(e.target.name));
        dispatch(changeUrlAction(SearchPageType.BASIC, history));
    };

    const renderSectionsTab = (): JSX.Element => {
        return <ContentSection/>;
    };

    const renderSpotlightTab = (): JSX.Element => {
        return <SpotlightSection/>;
    };

    const renderDetailsTab = (): JSX.Element => {
        return <>
            {showTechnicalDetails && <BaseDataRow/>}
            <TagsRow/>
            {selectedDoc.duplicates && selectedDoc.duplicates.length > 0 && (
                <DuplicatesList duplicates={selectedDoc.duplicates}/>
            )}
        </>;
    };

    const renderContent = (): JSX.Element => {
        switch (tabToRender) {
            case TabType.CONTENT: {
                return renderSectionsTab();
            }
            case TabType.SPOTLIGHT: {
                return renderSpotlightTab();
            }
            case TabType.DETAILS: {
                return renderDetailsTab();
            }
        }
    };

    return (
        <>
            {searchConfig.enabledDetailsTabs && (
                <div id={'search-result-details'} className={classNames(showDetails ? '' : 'hideContent')}>
                    <div id={'details-collapsed'} onClick={onExpand}
                         className={classNames(showDetails ? '' : 'hideContent')}>
                        <SvgIcon
                            icon={Icons.INFO}
                            className={'details-collapsed-info'}
                        />
                        <SvgIcon
                            icon={Icons.ARROWS}
                            className={'details-collapsed-arrows'}
                        />
                    </div>
                    <div id={'details'} className={classNames(showDetails ? '' : 'hideContent')}>
                        <div id={'details_header'}>
                            <div id={'details_title_container'}>
                                <FileTypeColumnControl
                                    key={selectedDoc.id}
                                    doc={selectedDoc as DeepReadonly<ProductDocument>}
                                    resultItemViewConfig={searchConfig.resultListFields}
                                ></FileTypeColumnControl>
                                <div id={'details_title'}>{selectedDoc?.title}</div>
                                <HibuButton
                                    size={'sm'}
                                    onClick={onExpand}
                                    icon={Icons.X}
                                    tooltipKey={'search.details.hideDetails'}
                                    visible
                                    className={'details-close'}
                                    placement={'left'}
                                />
                            </div>
                            <div id={'details_tabs'}>
                                {searchConfig.enabledDetailsTabs.includes(TabType.CONTENT) && (
                                    <Button
                                        onClick={onClick}
                                        name={TabType.CONTENT}
                                        className={'details-tab'}
                                        active={tabToRender === TabType.CONTENT}
                                    >
                                        {t('search.details.tabs.sections')}
                                    </Button>
                                )}
                                {searchConfig.enabledDetailsTabs.includes(TabType.SPOTLIGHT) && (
                                    <Button
                                        onClick={onClick}
                                        name={TabType.SPOTLIGHT}
                                        className={'details-tab'}
                                        active={tabToRender === TabType.SPOTLIGHT}
                                    >
                                        {t('search.details.tabs.terms')}
                                    </Button>
                                )}
                                {searchConfig.enabledDetailsTabs.includes(TabType.DETAILS) && (
                                    <Button
                                        onClick={onClick}
                                        name={TabType.DETAILS}
                                        className={'details-tab'}
                                        active={tabToRender === TabType.DETAILS}
                                    >
                                        {t('search.details.tabs.details')}
                                    </Button>
                                )}
                            </div>
                        </div>
                        <div id={'details_content'}>
                            {renderContent()}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
});
